import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { cachedFeed, completePairing, extractFeedKey, getFeedKey, loadFeed, saveFeedKey } from './api'
import type { Feed } from './types'

function route() {
  const hash = location.hash || '#/'
  if (hash.startsWith('#/subscribe/')) return { name: 'subscribe', value: decodeURIComponent(hash.slice(12)), transferSecret: '' }
  if (hash.startsWith('#/pair/')) {
    const [rawId, query = ''] = hash.slice(7).split('?', 2)
    return {
      name: 'pair',
      value: decodeURIComponent(rawId),
      transferSecret: new URLSearchParams(query).get('transfer') ?? '',
    }
  }
  return { name: 'viewer', value: '', transferSecret: '' }
}

export function App() {
  const [currentRoute, setCurrentRoute] = useState(route())
  useEffect(() => {
    const onHash = () => setCurrentRoute(route())
    addEventListener('hashchange', onHash)
    return () => removeEventListener('hashchange', onHash)
  }, [])

  if (currentRoute.name === 'subscribe') {
    saveFeedKey(currentRoute.value)
    history.replaceState(null, '', '/#/viewer')
    return <Viewer />
  }
  if (currentRoute.name === 'pair') return <Pairing pairingId={currentRoute.value} transferSecret={currentRoute.transferSecret} />
  return getFeedKey() ? <Viewer /> : <Subscribe />
}

function Subscribe() {
  const [value, setValue] = useState('')
  const submit = (event: FormEvent) => {
    event.preventDefault()
    const key = extractFeedKey(value)
    if (!key) return
    saveFeedKey(key)
    location.hash = '#/viewer'
    location.reload()
  }
  return (
    <main className="setup-page">
      <section className="setup-card">
        <div className="brand-lockup"><img className="brand-logo" src="/logo.png" alt="幼薇拾光" /><div><p className="eyebrow">YOUWEI</p><p className="brand-name">幼薇拾光</p></div></div>
        <h1>把我们的时光，轻轻拾回来。</h1>
        <p className="muted">粘贴你的私密订阅链接或订阅密钥。密钥只保存在这台设备上。</p>
        <form onSubmit={submit}>
          <input value={value} onChange={e => setValue(e.target.value)} placeholder="https://…/#/subscribe/…" autoFocus />
          <button type="submit">打开每日精选</button>
        </form>
      </section>
    </main>
  )
}

function Pairing({ pairingId, transferSecret }: { pairingId: string, transferSecret: string }) {
  const [key, setKey] = useState(getFeedKey())
  const [message, setMessage] = useState('确认后，电视将获得这份私密订阅。')
  const [done, setDone] = useState(false)
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    try {
      const normalized = extractFeedKey(key)
      if (!transferSecret) throw new Error('配对二维码已失效，请在大屏上重新生成')
      await completePairing(pairingId, normalized, transferSecret)
      saveFeedKey(normalized)
      setDone(true)
      setMessage('配对完成，可以回到电视。')
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '配对失败')
    }
  }
  return (
    <main className="setup-page">
      <section className="setup-card compact">
        <div className="brand-lockup small"><img className="brand-logo" src="/logo.png" alt="" /><div><p className="eyebrow">幼薇拾光</p><p className="brand-name">电视配对</p></div></div>
        <h1>{done ? '已经连好' : '把回忆送到大屏'}</h1>
        <p className="muted">{message}</p>
        {!done && <form onSubmit={submit}>
          <input value={key} onChange={e => setKey(e.target.value)} placeholder="订阅密钥或私密链接" autoFocus />
          <button type="submit">确认配对</button>
        </form>}
      </section>
    </main>
  )
}

function Viewer() {
  const [feed, setFeed] = useState<Feed | null>(cachedFeed())
  const [index, setIndex] = useState(0)
  const [playing, setPlaying] = useState(true)
  const [showCaption, setShowCaption] = useState(true)
  const [error, setError] = useState('')
  const refresh = useCallback(async () => {
    try {
      const next = await loadFeed(getFeedKey())
      setFeed(next)
      setIndex(value => Math.min(value, Math.max(0, next.items.length - 1)))
      setError('')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '暂时无法连接服务器')
    }
  }, [])
  useEffect(() => {
    refresh()
    const timer = window.setInterval(refresh, 15 * 60 * 1000)
    const resume = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    document.addEventListener('visibilitychange', resume)
    window.addEventListener('online', refresh)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', resume)
      window.removeEventListener('online', refresh)
    }
  }, [refresh])
  useEffect(() => {
    type WakeLockSentinel = { release: () => Promise<void> }
    type WakeLockNavigator = Navigator & { wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinel> } }
    let sentinel: WakeLockSentinel | undefined
    const acquire = async () => {
      if (document.visibilityState !== 'visible' || sentinel) return
      try {
        sentinel = await (navigator as WakeLockNavigator).wakeLock?.request('screen')
      } catch {
        sentinel = undefined
      }
    }
    const visibility = () => {
      if (document.visibilityState === 'visible') void acquire()
      else {
        void sentinel?.release()
        sentinel = undefined
      }
    }
    void acquire()
    document.addEventListener('visibilitychange', visibility)
    return () => {
      document.removeEventListener('visibilitychange', visibility)
      void sentinel?.release()
    }
  }, [])
  const count = feed?.items.length ?? 0
  const next = useCallback(() => count && setIndex(value => (value + 1) % count), [count])
  const previous = useCallback(() => count && setIndex(value => (value - 1 + count) % count), [count])
  useEffect(() => {
    if (!playing || count < 2) return
    const timer = window.setInterval(next, 30_000)
    return () => clearInterval(timer)
  }, [playing, count, next])
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') next()
      if (event.key === 'ArrowLeft') previous()
      if (event.key === ' ') setPlaying(value => !value)
      if (event.key.toLowerCase() === 'h') setShowCaption(value => !value)
      if (event.key.toLowerCase() === 'f') document.documentElement.requestFullscreen?.()
    }
    addEventListener('keydown', handler)
    return () => removeEventListener('keydown', handler)
  }, [next, previous])
  const item = useMemo(() => feed?.items[index], [feed, index])

  if (!item) return <main className="loading"><div className="spinner" /><p>{error || '正在取回今天的照片…'}</p></main>
  const captureDate = formatCaptureDate(item.capture_date)
  const captureMeta = [
    captureDate ? `拍摄于 ${captureDate}` : '拍摄时间未记录',
    item.city ? `拍摄地 · ${item.city}` : '',
  ].filter(Boolean).join('  ·  ')
  return (
    <main className="viewer" style={{ backgroundColor: item.dominant_color }}>
      <img className="backdrop" src={item.asset_url} alt="" aria-hidden="true" />
      <img className="photo" src={item.asset_url} alt={item.caption} />
      <div className="vignette" />
      {showCaption && <aside className="caption-panel">
        <p className="side-caption">{item.side_caption}</p>
        <p className="meta">{captureMeta}</p>
      </aside>}
      <div className="topbar"><span>{captureMeta}</span><span>{index + 1} / {count}</span></div>
      <nav className="controls" aria-label="播放控制">
        <button onClick={previous} aria-label="上一张">←</button>
        <button onClick={() => setPlaying(value => !value)} aria-label={playing ? '暂停' : '播放'}>{playing ? 'Ⅱ' : '▶'}</button>
        <button onClick={next} aria-label="下一张">→</button>
        <button onClick={() => setShowCaption(value => !value)} aria-label="显示或隐藏文案">文</button>
        <button onClick={() => document.documentElement.requestFullscreen?.()} aria-label="全屏">↗</button>
      </nav>
      {error && <div className="offline-note">离线浏览 · {error}</div>}
    </main>
  )
}

function formatCaptureDate(value: string): string {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return value
  return `${match[1]}年${Number(match[2])}月${Number(match[3])}日`
}
